// =====================================================
// TRACK CHANGES — wrapped as ES module from src/js/docx-tracking.js
// Supports native Word track changes (w:ins, w:del, w:pPrChange, w:rPrChange).
// =====================================================

import { xmlEscape, W_NS, parseEl, parseFrag, makeEl, serializeClean, tagCrossRelIds } from './xml-helpers';
import { pieceSegments, pairSegments } from './segments';
import { sideOf, diffPieces, trailingGap, layoutOf, gapsDiffer } from './pieces';
import { wordOf } from './word-index';
import type { Piece } from './pieces';
import { placeInline, cmpLoc } from './inline';
import type { Loc, PlacedEvent } from './inline';
import { addDeleteMarkup, addInsertMarkup, addMoveFromMarkup, addMoveToMarkup, applyMarkupToAllRuns, normalizeRPrOrder, rPrIdx } from './docx-markup';
import { recMinted, recChange, recSetCurrent, recGetCurrent, recCurrentBlock, recBlock } from './change-record';
import type { RecordedType } from './change-record';
import type { DiffEntry, DiffParagraph, RichParagraph, RichToken, InlineEvent } from '../types';

/** Replace an element with a same-content element under a new qualified name
 *  (DOM has no portable renameNode). Used to turn <w:t> into <w:delText> etc. */
function renameEl(el: Element, newName: string): Element {
  const repl = makeEl(el.ownerDocument!, newName);
  for (const a of Array.from(el.attributes)) repl.setAttributeNS(a.namespaceURI, a.name, a.value);
  while (el.firstChild) repl.appendChild(el.firstChild);
  el.parentNode!.replaceChild(repl, el);
  return repl;
}

/** Children elements of `parent` with the given local name, in the w: namespace. */
function wEls(parent: Element | Document, local: string): Element[] {
  return Array.from((parent as Element).getElementsByTagNameNS(W_NS, local));
}

/** Parse an element fragment and import it into `doc` so it can be appended there. */
function importEl(doc: Document, xml: string): Element {
  return doc.importNode(parseEl(xml), true) as Element;
}

/** First direct child element of `parent` with the given w:-namespace local name. */
function directChild(parent: Element, local: string): Element | null {
  for (const c of Array.from(parent.childNodes)) {
    const e = c as Element;
    if (e.nodeType === 1 && e.localName === local && e.namespaceURI === W_NS) return e;
  }
  return null;
}

/** Set the standard revision attributes (id/author/date) on a change element. */
function setRevAttrs(el: Element): void {
  el.setAttribute('w:id', String(getNextRevId()));
  el.setAttribute('w:author', _tcAuthor);
  el.setAttribute('w:date', _tcDate);
}

let _tcRevId = 0;
let _tcAuthor = 'Fredline';
let _tcDate = '';
let _useTrackChanges = false;
// The writer run for its change record alone (recordChanges): what it records, and the revision ids
// it would give, without the XML.
let _recordOnly = false;
export function setRecordOnly(on: boolean): void { _recordOnly = on; }
export function getRecordOnly(): boolean { return _recordOnly; }
// Combine / "track all changes" mode: preserve the revised doc's incoming tracked
// changes (with their original authors) instead of accepting them into the baseline.
let _preserveIncoming = false;
export function setPreserveIncoming(on: boolean): void { _preserveIncoming = on; }
export function getPreserveIncoming(): boolean { return _preserveIncoming; }

/** True if a paragraph's raw XML carries any pre-existing tracked-change region. */
export function hasIncomingRevisions(rawXml: string | null | undefined): boolean {
  return !!rawXml && /<w:(ins|del|moveFrom|moveTo)\b/.test(rawXml);
}

/**
 * The paragraph's text with its tracked changes REJECTED — i.e. the state before the
 * incoming author edited it: inserted runs dropped, deleted runs (delText) restored.
 * Used to decide whether the incoming changes alone explain the original→revised
 * difference (if so, the revised paragraph can be emitted verbatim under its authors).
 */
export function rejectedRunText(rawXml: string): string {
  try {
    const p = parseEl(rawXml);
    let out = '';
    for (const run of Array.from(p.getElementsByTagNameNS(W_NS, 'r'))) {
      let underIns = false;
      for (let n: Node | null = run.parentNode; n && (n as Element).nodeType === 1; n = n.parentNode) {
        const ln = (n as Element).localName;
        if (ln === 'ins' || ln === 'moveTo') { underIns = true; break; }
      }
      if (underIns) continue; // inserted text is rejected away
      for (const c of Array.from(run.childNodes)) {
        const e = c as Element;
        if (e.nodeType !== 1) continue;
        if (e.localName === 't' || e.localName === 'delText') out += e.textContent;
        else if (e.localName === 'tab') out += '\t';
        else if (e.localName === 'br') out += '\n';
      }
    }
    return out;
  } catch { return ''; }
}

/** A fresh revision id; it belongs to the change being written (change-record.ts). */
function getNextRevId(): number { const id = _tcRevId++; recMinted(id); return id; }
export function resetRevIdCounter(): void { _tcRevId = 0; }
/** How many tracked format changes (rPrChange) `diffRunsXml` would write for this diff, without
 *  writing anything: a dry run, the revision counter put back after. For the summary page, which
 *  is built before the footnotes are written. */
export function countFormatChanges(diff: DiffEntry[], origRich: any, revRich: any): number {
  const saved = _tcRevId, block = recCurrentBlock(), cur = recGetCurrent();
  recBlock(-1);   // a dry run records nothing
  try { return (diffRunsXml(diff, origRich, revRich).match(/<w:rPrChange\b/g) || []).length; }
  finally { _tcRevId = saved; recBlock(block); recSetCurrent(cur); }
}

/**
 * Seed the revision-id counter so newly-created <w:ins>/<w:del>/<w:*Change>
 * ids start above any revision ids already present in the source documents.
 * Without this, comparing docs that ALREADY contain tracked changes produces
 * duplicate w:id values (preserved-original ids collide with freshly minted
 * ones), which Word can flag as corrupt.
 */
export function setRevIdStart(n: number): void { if (n > _tcRevId) _tcRevId = n; }
/** The id the counter gives out next (nothing is given out). */
export function peekRevId(): number { return _tcRevId; }

export function setTrackChangesMode(useTC: boolean, author?: string, date?: string): void {
  _useTrackChanges = useTC;
  if (author) _tcAuthor = author;
  if (date) _tcDate = date;
}

export function getTrackChangesMode(): boolean {
  return _useTrackChanges;
}

/** A run's text as Word holds it: a tab is <w:tab/>, a line break <w:br/> (a page break \f and a
 *  column break \v, as the parser records them, typed), never a character inside <w:t> (Word shows
 *  a tab character there as a space). `tag` is t or delText. */
export function textXml(text: string, tag: 't' | 'delText' = 't'): string {
  if (!/[\t\n\f\v]/.test(text)) return `<w:${tag} xml:space="preserve">${xmlEscape(text)}</w:${tag}>`;
  return text.split(/([\t\n\f\v])/).map(part => part === '\t' ? '<w:tab/>' : part === '\n' ? '<w:br/>'
    : part === '\f' ? '<w:br w:type="page"/>' : part === '\v' ? '<w:br w:type="column"/>'
    : part ? `<w:${tag} xml:space="preserve">${xmlEscape(part)}</w:${tag}>` : '').join('');
}

export function buildRun(text: string, rPrXml: string): string {
  const rPr = normalizeRPrOrder(rPrXml || '');
  return `<w:r>${rPr}${textXml(text)}</w:r>`;
}

/**
 * Remove any w:pPrChange / w:rPrChange elements (and their nested content) from a
 * pPr/rPr string. The "prior state" embedded inside a *Change element must itself
 * be clean — and the source may already carry a tracked-format change.
 */
function stripChangeChildren(xml: string, tags: string[] = ['pPrChange', 'rPrChange']): string {
  for (const tag of tags) {
    const re = new RegExp(`<w:${tag}\\b[^>]*[^/]>[\\s\\S]*?<\\/w:${tag}>`, 'g');
    let prev: string;
    do { prev = xml; xml = xml.replace(re, ''); } while (xml !== prev);
    xml = xml.replace(new RegExp(`<w:${tag}\\b[^>]*/>`, 'g'), '');
  }
  return xml;
}

// Children the schema allows in the run-properties snapshot inside <w:rPrChange>
// (CT_RPrOriginal = EG_RPrBase). Some generators emit non-schema siblings such as
// <w:highlightCs/>; Word tolerates them in a live rPr, but the snapshot is content
// Fredline constructs, so keep it to what the schema permits.
const RPR_ORIGINAL_CHILDREN = new Set(['rStyle', 'rFonts', 'b', 'bCs', 'i', 'iCs', 'caps', 'smallCaps', 'strike', 'dstrike',
  'outline', 'shadow', 'emboss', 'imprint', 'noProof', 'snapToGrid', 'vanish', 'webHidden', 'color', 'spacing', 'w', 'kern',
  'position', 'sz', 'szCs', 'highlight', 'u', 'effect', 'bdr', 'shd', 'fitText', 'vertAlign', 'rtl', 'cs', 'em', 'lang',
  'eastAsianLayout', 'specVanish', 'oMath']);
/** The "previous run properties" snapshot for a <w:rPrChange>: no nested *Change, only schema children. */
function rPrSnapshot(oldRPrXml: string | undefined | null): string {
  if (!oldRPrXml) return '<w:rPr/>';
  let xml = stripChangeChildren(oldRPrXml);
  // drop w:-namespace children the snapshot type does not allow (self-closing or container form)
  xml = xml.replace(/<w:([A-Za-z0-9]+)\b[^>]*\/>/g, (m, tag) => tag === 'rPr' || RPR_ORIGINAL_CHILDREN.has(tag) ? m : '');
  xml = xml.replace(/<w:([A-Za-z0-9]+)\b[^>]*>[\s\S]*?<\/w:\1>/g, (m, tag) => tag === 'rPr' || RPR_ORIGINAL_CHILDREN.has(tag) ? m : '');
  return xml;
}

/**
 * Reduce a pPr to the CT_PPrBase subset that is valid as the "previous properties"
 * snapshot inside a <w:pPrChange>. CT_PPrBase — unlike the full CT_PPr — does NOT
 * permit the paragraph-mark <w:rPr> or a <w:sectPr>; leaving either in makes Word's
 * schema validator reject the file (and other tools may refuse it outright). Also
 * drops any nested *Change elements via stripChangeChildren.
 */
function stripToPPrBase(pPrXml: string): string {
  let xml = stripChangeChildren(pPrXml);
  for (const tag of ['rPr', 'sectPr']) {
    // Self-closing first (e.g. <w:rPr/>), then the container form. The container
    // pattern matches an attribute-less <w:rPr> too, which the earlier `[^>]*[^/]>`
    // pattern missed — that miss is exactly why the paragraph-mark rPr survived.
    xml = xml.replace(new RegExp(`<w:${tag}\\b[^>]*/>`, 'g'), '');
    xml = xml.replace(new RegExp(`<w:${tag}\\b[^>]*>[\\s\\S]*?</w:${tag}>`, 'g'), '');
  }
  return xml;
}

// CT_PPr child order — needed to insert a <w:pBdr> at its schema position.
const PPR_ORDER = ['pStyle', 'keepNext', 'keepLines', 'pageBreakBefore', 'framePr', 'widowControl',
  'numPr', 'suppressLineNumbers', 'pBdr', 'shd', 'tabs', 'suppressAutoHyphens', 'kinsoku', 'wordWrap',
  'overflowPunct', 'topLinePunct', 'autoSpaceDE', 'autoSpaceDN', 'bidi', 'adjustRightInd', 'snapToGrid',
  'spacing', 'ind', 'contextualSpacing', 'mirrorIndents', 'suppressOverlap', 'jc', 'textDirection',
  'textAlignment', 'textboxTightWrap', 'outlineLvl', 'divId', 'cnfStyle', 'rPr', 'sectPr', 'pPrChange'];
const pPrIdx = (local: string | null): number => { const i = PPR_ORDER.indexOf(local || ''); return i < 0 ? PPR_ORDER.length : i; };

/** Reorder an element's children into schema order (stable for ties/unknowns). */
function reorderChildren(el: Element, idx: (local: string | null) => number, dedupe = false): void {
  const kids = Array.from(el.childNodes).filter(c => (c as Element).nodeType === 1) as Element[];
  if (kids.length < 2) return;
  if (dedupe) {
    // Each property element may appear once; some tools write e.g. <w:i/> twice.
    const seen = new Set<string>();
    for (const k of kids) {
      const key = (k.namespaceURI || '') + ':' + k.localName;
      if (seen.has(key)) el.removeChild(k); else seen.add(key);
    }
  }
  const live = Array.from(el.childNodes).filter(c => (c as Element).nodeType === 1) as Element[];
  const sorted = live.map((k, i) => ({ k, i })).sort((a, b) => (idx(a.k.localName) - idx(b.k.localName)) || (a.i - b.i));
  if (sorted.every((s, i) => s.k === live[i])) return;
  for (const { k } of sorted) el.appendChild(k);
}

/**
 * Prepare source XML that is emitted verbatim (an unchanged paragraph, table row or
 * passthrough block) for the output:
 *  - In Compare mode, ACCEPT any tracked changes the source paragraph already carries.
 *    Every changed paragraph is rebuilt from the accepted text, so leaving a source's
 *    own <w:ins>/<w:del> on its unchanged paragraphs leaked third-party revisions into
 *    a single-author redline and made "reject all" diverge from the original. Combine
 *    mode keeps them on purpose. In Compare mode the generator has already accepted
 *    every incoming change up front (acceptIncomingInPara), so this is a backstop. It
 *    must not drop pPrChange/rPrChange: its input can be the format-change emitter's
 *    output, which carries Fredline's own.
 *  - Reorder every pPr/rPr into CT_PPr/CT_RPr schema order. Word tolerates the
 *    out-of-order children many authoring tools write, but the OOXML validator (and
 *    stricter consumers) reject them; the output should be cleaner than its inputs.
 */
export function preparePassthrough(rawXml: string): string {
  if (!rawXml) return rawXml;
  const xml = _preserveIncoming ? rawXml : (/<w:(ins|del|moveFrom|moveTo)\b/.test(rawXml) ? flattenAcceptRevisions(rawXml) : rawXml);
  return normalizePropOrder(xml);
}

// CT_Borders child order (tcBorders / tblBorders).
const BORDER_ORDER = ['top', 'start', 'left', 'bottom', 'end', 'right', 'insideH', 'insideV', 'tl2br', 'tr2bl'];
const borderIdx = (local: string | null): number => { const i = BORDER_ORDER.indexOf(local || ''); return i < 0 ? BORDER_ORDER.length : i; };
// CT_TblPr child order.
const TBLPR_ORDER = ['tblStyle', 'tblpPr', 'tblOverlap', 'bidiVisual', 'tblStyleRowBandSize', 'tblStyleColBandSize', 'tblW', 'jc',
  'tblCellSpacing', 'tblInd', 'tblBorders', 'shd', 'tblLayout', 'tblCellMar', 'tblLook', 'tblCaption', 'tblDescription', 'tblPrChange'];
const tblPrIdx = (local: string | null): number => { const i = TBLPR_ORDER.indexOf(local || ''); return i < 0 ? TBLPR_ORDER.length : i; };

/**
 * Schema-clean any emitted body XML without changing its meaning: reorder pPr/rPr/
 * border children into schema order and repair the source-authored attribute values
 * Word tolerates but the OOXML schema rejects (a '#'-prefixed hex colour, fractional
 * twip widths). Applied to everything the generator emits — source passthrough and
 * rebuilt paragraphs alike — so the output validates even when the inputs don't.
 */
export function normalizePropOrder(xml: string): string {
  if (!xml) return xml;
  // The generator streams tables as an opening `<w:tbl>`+tblPr part, row parts, and a
  // closing `</w:tbl>` part — those two aren't parseable fragments; leave them alone.
  if ((xml.startsWith('<w:tbl>') && !xml.endsWith('</w:tbl>')) || xml.trim() === '</w:tbl>') return xml;
  let out = xml;
  // ST_HexColor never takes a '#': some authoring tools write w:color="#000000" on
  // borders/shading/run colours (w:val on <w:color>, w:color on borders, w:fill on shd).
  if (/="#[0-9A-Fa-f]{6}"/.test(out)) out = out.replace(/(\sw:(?:val|color|fill|themeColor)=")#([0-9A-Fa-f]{6}")/g, '$1$2');
  // Fractional twips/half-points ("174.00000000000006") in any numeric measure attribute
  // are schema-invalid (Int32/UInt32/twips); a unit-bearing value ("1.5in") is untouched.
  if (/\sw:[a-zA-Z]+="-?\d+\.\d+"/.test(out)) out = out.replace(/(\sw:[a-zA-Z]+=")(-?\d+)\.\d+(")/g, '$1$2$3');
  // Legacy VML written by some DMS integrations carries an undeclared `ID="DocID"` and a
  // `path` on <v:rect> (path is only valid on <v:shape>). Word ignores both; drop them.
  if (/<v:(?:rect|roundrect|oval|line)\b[^>]*\s(?:ID|path)="/.test(out)) {
    out = out.replace(/(<v:(?:rect|roundrect|oval|line)\b(?:[^>]*?))\s(?:ID|path)="[^"]*"/g, '$1')
             .replace(/(<v:(?:rect|roundrect|oval|line)\b(?:[^>]*?))\s(?:ID|path)="[^"]*"/g, '$1');
  }
  // <w:shd> requires w:val (the pattern); tools that only set the fill omit it.
  if (/<w:shd\b(?![^>]*\sw:val=)/.test(out)) out = out.replace(/<w:shd\b(?![^>]*\sw:val=)/g, '<w:shd w:val="clear"');
  // ST_OnOff in the strict schema is 1/0/on/off; "true"/"false" (written by some tools
  // on e.g. <w:cantSplit>) fails validation though Word accepts it.
  // The Microsoft365 validator rejects both "true" and "1" on <w:cantSplit>/<w:tblHeader>
  // (row-property on/off), so express "on" the canonical way: the bare element.
  if (/<w:(?:cantSplit|tblHeader)\s+w:val="(?:true|1|on)"/.test(out)) out = out.replace(/<w:(cantSplit|tblHeader)\s+w:val="(?:true|1|on)"\s*\/>/g, '<w:$1/>');
  // … and "off" is the only way to write it off ("false" and "0" fail the same check)
  if (/<w:(?:cantSplit|tblHeader)\s+w:val="(?:false|0)"/.test(out)) out = out.replace(/<w:(cantSplit|tblHeader)\s+w:val="(?:false|0)"\s*\/>/g, '<w:$1 w:val="off"/>');
  if (/\sw:val="(?:true|false)"/.test(out)) out = out.replace(/(<w:(?:hidden|bidiVisual|keepNext|keepLines|widowControl|b|bCs|i|iCs|caps|smallCaps|strike|dstrike|vanish|noProof|snapToGrid|contextualSpacing|mirrorIndents|suppressAutoHyphens|pageBreakBefore|suppressLineNumbers|webHidden|specVanish)\b[^>]*\sw:val=")(true|false)(")/g, (_m, pre, v, post) => pre + (v === 'true' ? '1' : '0') + post);
  if (!/<w:(pPr|rPr|tblPr|tcBorders|tblBorders)\b/.test(out)) return out;
  try {
    // A part may hold several top-level elements (e.g. a deleted row followed by its
    // inserted replacement, or the whole summary section) — normalize every root, and
    // never drop siblings.
    const roots = parseFrag(out);
    if (!roots.length) return out;
    const pieces: string[] = [];
    for (const root of roots) {
      // A paragraph's properties come first (CT_P). Some tools write a bookmarkStart before
      // them; carried into a paragraph Fredline marks, that is a schema error in our output.
      const paras = Array.from(root.getElementsByTagNameNS(W_NS, 'p'));
      if (root.localName === 'p' && root.namespaceURI === W_NS) paras.push(root);
      for (const para of paras) {
        let first = para.firstChild;
        while (first && first.nodeType !== 1) first = first.nextSibling;
        const pPr = Array.from(para.childNodes).find((n: any) => n.nodeType === 1 && n.localName === 'pPr' && n.namespaceURI === W_NS);
        if (pPr && first && first !== pPr) para.insertBefore(pPr, first);
      }
      for (const p of Array.from(root.getElementsByTagNameNS(W_NS, 'pPr'))) reorderChildren(p, pPrIdx, true);
      for (const r of Array.from(root.getElementsByTagNameNS(W_NS, 'rPr'))) reorderChildren(r, rPrIdx, true);
      for (const t of Array.from(root.getElementsByTagNameNS(W_NS, 'tblPr'))) reorderChildren(t, tblPrIdx, true);
      for (const tag of ['tcBorders', 'tblBorders']) for (const b of Array.from(root.getElementsByTagNameNS(W_NS, tag))) reorderChildren(b, borderIdx, true);
      // getElementsByTagNameNS never returns the root itself — handle a bare property
      // fragment (the table opener passes its tblPr string directly).
      if (root.localName === 'pPr') reorderChildren(root, pPrIdx, true);
      if (root.localName === 'rPr') reorderChildren(root, rPrIdx, true);
      if (root.localName === 'tblPr') reorderChildren(root, tblPrIdx, true);
      pieces.push(serializeClean(root));
    }
    return pieces.join('');
  } catch {
    return out;
  }
}

/**
 * Add an OOXML change bar (a <w:bar> margin rule via <w:pBdr>) to a paragraph. Track-
 * changes output gets change bars from Word automatically (any line with a revision),
 * but the flat markup/redline mode has no revisions, so Word draws nothing — this adds
 * the bar manually so a changed paragraph is flagged in the margin either way. No-ops on
 * anything that isn't a <w:p> (e.g. a whole <w:tr> passed for a table-row change).
 */
export function addChangeBarToParagraph(paraXml: string): string {
  try {
    const p = parseEl(paraXml);
    if (p.localName !== 'p' || p.namespaceURI !== W_NS) return paraXml;
    const doc = p.ownerDocument!;
    let pPr = directChild(p, 'pPr');
    if (!pPr) { pPr = makeEl(doc, 'w:pPr'); p.insertBefore(pPr, p.firstChild); }
    let pBdr = directChild(pPr, 'pBdr');
    if (!pBdr) {
      pBdr = makeEl(doc, 'w:pBdr');
      const idx = pPrIdx('pBdr');
      let placed = false;
      for (const c of Array.from(pPr.childNodes)) {
        const ce = c as Element;
        if (ce.nodeType === 1 && pPrIdx(ce.localName) > idx) { pPr.insertBefore(pBdr, ce); placed = true; break; }
      }
      if (!placed) pPr.appendChild(pBdr);
    }
    if (!directChild(pBdr, 'left')) {
      // Word ignores the legacy <w:bar> change-bar border for display, so use a left
      // border offset into the margin with w:space — Word always renders that as a
      // vertical rule, which reads as a change bar beside the edited paragraph. <w:left>
      // is the 2nd child of CT_PBdr (top,left,bottom,right,between,bar).
      const left = makeEl(doc, 'w:left');
      left.setAttribute('w:val', 'single');
      left.setAttribute('w:sz', '18');    // 2.25pt rule
      left.setAttribute('w:space', '20');  // ~20pt into the left margin
      left.setAttribute('w:color', 'auto');
      const top = directChild(pBdr, 'top');
      if (top && top.nextSibling) pBdr.insertBefore(left, top.nextSibling);
      else if (top) pBdr.appendChild(left);
      else pBdr.insertBefore(left, pBdr.firstChild);
    }
    return serializeClean(p);
  } catch { return paraXml; }
}

/** An inserted or deleted body paragraph with no text and no embedded object, from a .docx (it
 *  has XML). Track mode writes it with its paragraph mark revised; markup mode skips it. The
 *  change list (changes/change-list.ts) mirrors this rule. */
export function emptyTrackedBlank(para: DiffParagraph, raw: DiffParagraph = para): boolean {
  if (para.type !== 'para-delete' && para.type !== 'para-insert') return false;
  const rich = para.type === 'para-delete' ? para.origRich : para.revRich;
  // a blank paragraph whose own mark the draft deleted or moved away (tracked) is gone once its
  // changes are accepted: nothing to write. `raw` is the paragraph before the drafts' own
  // changes were accepted (acceptIncomingInPara), which still shows the mark's revision.
  const rawRich = para.type === 'para-delete' ? raw.origRich : raw.revRich;
  const markGone = /<w:(?:del|moveFrom)\b/.test((stripChangeChildren(rawRich?.pPrXml || '').match(/<w:rPr\b[^>]*>([\s\S]*?)<\/w:rPr>/) || [])[1] || '');
  return !!rich?.rawXml && !rich.isTableRow && !rich.isPassthrough && !(para.text || '').trim() && !markGone
    && !/<(?:w:drawing|w:pict|w:object|m:oMath|mc:AlternateContent)\b/.test(rich.rawXml) && /^\s*<w:p\b/.test(rich.rawXml);
}


/**
 * A wholly inserted or deleted table row: mark the row itself (w:trPr/w:ins or w:del), as Word
 * does, so accepting a deletion (or rejecting an insertion) removes the row instead of leaving
 * an empty one. Track mode only; a row already marked is left alone.
 */
export function addRowRevision(rowXml: string, kind: 'inserted' | 'deleted'): string {
  if (!_useTrackChanges) return rowXml;
  const open = rowXml.match(/^\s*<w:tr\b[^>]*>/);
  if (!open) return rowXml;
  let at = open[0].length;
  const prEx = rowXml.slice(at).match(/^\s*<w:tblPrEx\b[^>]*(?:\/>|>[\s\S]*?<\/w:tblPrEx>)/);
  if (prEx) at += prEx[0].length;
  const tag = kind === 'inserted' ? 'ins' : 'del';
  const mark = `<w:${tag} w:id="${getNextRevId()}" w:author="${xmlEscape(_tcAuthor)}" w:date="${_tcDate}"/>`;
  const trPr = rowXml.slice(at).match(/^\s*<w:trPr\b[^>]*>([\s\S]*?)<\/w:trPr>|^\s*<w:trPr\s*\/>/);
  if (!trPr) return rowXml.slice(0, at) + `<w:trPr>${mark}</w:trPr>` + rowXml.slice(at);
  if (/<w:(?:ins|del)\b/.test((trPr[1] || '').replace(/<w:trPrChange\b[\s\S]*$/, ''))) return rowXml;
  const whole = trPr[0];
  // ins / del come last in CT_TrPr, before a trPrChange
  const inner = whole.endsWith('/>') ? '' : (trPr[1] || '');
  const cut = inner.search(/<w:trPrChange\b/);
  const next = cut >= 0 ? inner.slice(0, cut) + mark + inner.slice(cut) : inner + mark;
  const head = whole.match(/^\s*<w:trPr\b[^>]*?(?=\/?>)/)![0];
  return rowXml.slice(0, at) + `${head}>${next}</w:trPr>` + rowXml.slice(at + whole.length);
}

/**
 * The space at a paragraph split or merge. Splitting "A. B" into "A." and "B" deletes the space
 * between them; merging them inserts one. The engine compares words, so the space is not one of
 * its changes; track it next to the paragraph mark, so that rejecting the split (or accepting the
 * merge) gives "A. B", not "A.B". Nothing is added when either side already has whitespace at
 * the break (`hereEnds`, `nextStarts`). Track mode only.
 */
export function addParagraphMarkSpace(paraXml: string, kind: 'inserted' | 'deleted', hereEnds: boolean, nextStarts: boolean): string {
  if (!_useTrackChanges || hereEnds || nextStarts) return paraXml;
  const id = getNextRevId();
  const run = kind === 'inserted'   // a split: the space was deleted
    ? `<w:del w:id="${id}" w:author="${xmlEscape(_tcAuthor)}" w:date="${_tcDate}"><w:r><w:delText xml:space="preserve"> </w:delText></w:r></w:del>`
    : `<w:ins w:id="${id}" w:author="${xmlEscape(_tcAuthor)}" w:date="${_tcDate}"><w:r><w:t xml:space="preserve"> </w:t></w:r></w:ins>`;
  return paraXml.replace(/<\/w:p>\s*$/, run + '</w:p>');
}

/**
 * Record a paragraph SPLIT or MERGE the way Word does: a revision on the paragraph
 * MARK, with every word left untouched. Track-changes mode puts a self-closing
 * <w:ins/> (new break) or <w:del/> (removed break) FIRST inside the paragraph-mark
 * <w:pPr><w:rPr> — CT_ParaRPr orders ins/del/moveFrom/moveTo before everything else.
 * Markup mode has no paragraph-mark revisions, so it appends a visible pilcrow run
 * styled as an insertion/deletion and adds the change bar.
 */
export function addParagraphMarkRevision(paraXml: string, kind: 'inserted' | 'deleted'): string {
  try {
    if (!_useTrackChanges) {
      const mark = kind === 'inserted' ? addInsertMarkup('') : addDeleteMarkup('');
      const withGlyph = paraXml.replace(/<\/w:p>\s*$/, `<w:r>${mark}<w:t>\u00B6</w:t></w:r></w:p>`);
      return addChangeBarToParagraph(withGlyph);
    }
    const p = parseEl(paraXml);
    if (p.localName !== 'p' || p.namespaceURI !== W_NS) return paraXml;
    const doc = p.ownerDocument!;
    let pPr = directChild(p, 'pPr');
    if (!pPr) { pPr = makeEl(doc, 'w:pPr'); p.insertBefore(pPr, p.firstChild); }
    let rPr = directChild(pPr, 'rPr');
    if (!rPr) {
      rPr = makeEl(doc, 'w:rPr');
      // rPr sits late in CT_PPr: before sectPr / pPrChange, after everything else
      const idx = pPrIdx('rPr');
      let placed = false;
      for (const c of Array.from(pPr.childNodes)) {
        const ce = c as Element;
        if (ce.nodeType === 1 && pPrIdx(ce.localName) > idx) { pPr.insertBefore(rPr, ce); placed = true; break; }
      }
      if (!placed) pPr.appendChild(rPr);
    }
    // never double-mark
    if (directChild(rPr, 'ins') || directChild(rPr, 'del')) return paraXml;
    const rev = makeEl(doc, kind === 'inserted' ? 'w:ins' : 'w:del');
    setRevAttrs(rev);
    rPr.insertBefore(rev, rPr.firstChild);
    return serializeClean(p);
  } catch {
    return paraXml;
  }
}

/** Build a run with an rPrChange element inside the rPr (for tracking formatting changes). */
function buildRunWithRPrChange(text: string, newRPrXml: string, oldRPrXml: string): string {
  // If the rPr already records a tracked format change, keep it as-is — OOXML
  // allows only one rPrChange per rPr, and injecting into the existing one's
  // nested rPr corrupts the XML.
  if (newRPrXml && /<w:rPrChange\b/.test(newRPrXml)) {
    return `<w:r>${newRPrXml}${textXml(text)}</w:r>`;
  }
  // Build the rPr (with the rPrChange appended as its last child) via nodes, then
  // assemble the run with a string template so text escaping stays consistent with
  // the other run builders.
  return `<w:r>${injectRPrChange_node(newRPrXml, oldRPrXml)}${textXml(text)}</w:r>`;
}

function injectRPrChange_node(newRPrXml: string, oldRPrXml: string): string {
  const rPr = parseEl(newRPrXml || '<w:rPr/>');
  const change = makeEl(rPr.ownerDocument!, 'w:rPrChange');
  setRevAttrs(change);
  change.appendChild(importEl(rPr.ownerDocument!, rPrSnapshot(oldRPrXml)));
  rPr.appendChild(change); // append as last child — correct position by construction
  return serializeClean(rPr);
}

export function buildRevisionRun(text: string, rPrXml: string, type: string): string {
  const rPr = normalizeRPrOrder(rPrXml || '');

  if (_useTrackChanges) {
    if (type === 'delete' || type === 'move-from') {
      const id = getNextRevId();
      return `<w:del w:id="${id}" w:author="${xmlEscape(_tcAuthor)}" w:date="${_tcDate}">` +
             `<w:r>${rPr}${textXml(text, 'delText')}</w:r></w:del>`;
    } else if (type === 'insert' || type === 'move-to') {
      const id = getNextRevId();
      return `<w:ins w:id="${id}" w:author="${xmlEscape(_tcAuthor)}" w:date="${_tcDate}">` +
             `<w:r>${rPr}${textXml(text)}</w:r></w:ins>`;
    }
    return `<w:r>${rPr}${textXml(text)}</w:r>`;
  } else {
    if (type === 'delete') return buildRun(text, addDeleteMarkup(rPr));
    if (type === 'insert') return buildRun(text, addInsertMarkup(rPr));
    if (type === 'move-from') return buildRun(text, addMoveFromMarkup(rPr));
    if (type === 'move-to') return buildRun(text, addMoveToMarkup(rPr));
    return buildRun(text, rPr);
  }
}

/**
 * Accept any tracked changes already present in a paragraph's rawXml, leaving a
 * clean paragraph with no revision markup: deleted content (w:del / w:moveFrom,
 * including anything nested inside) is removed, inserted content (w:ins / w:moveTo)
 * is unwrapped and kept, and self-closing paragraph-mark markers are dropped.
 *
 * This must run before we re-mark a whole paragraph as a Fredline insertion or
 * deletion: wrapping a run that is ALREADY inside a source <w:ins>/<w:del> would
 * nest revisions (e.g. <w:ins author="Reviewer"><w:ins author="Fredline">…),
 * which is invalid OOXML and makes Word report "unreadable content".
 */
export function flattenAcceptRevisions(rawXml: string): string {
  return flattenAcceptRevisions_node(rawXml);
}

function flattenAcceptRevisions_node(rawXml: string): string {
  const p = parseEl(rawXml);
  acceptInsDelIn(p);
  return serializeClean(p);
}

/** Accept every w:ins/w:del/w:moveFrom/w:moveTo below `root`, in place. */
function acceptInsDelIn(root: Element): void {
  // Remove deleted regions entirely (w:del / w:moveFrom, container or self-closing).
  for (const tag of ['del', 'moveFrom']) {
    for (const el of wEls(root, tag)) if (el.parentNode) el.parentNode.removeChild(el);
  }
  // Unwrap inserted/moved-in regions: move children out, drop the wrapper.
  for (const tag of ['ins', 'moveTo']) {
    let els = wEls(root, tag);
    while (els.length) {
      for (const el of els) {
        if (!el.parentNode) continue;
        while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el);
        el.parentNode.removeChild(el);
      }
      els = wEls(root, tag);
    }
  }
}

// ── Compare mode: accept every incoming tracked change ───────────────────────
// A Compare-mode redline carries Fredline's revisions only. flattenAcceptRevisions
// covers the content revisions; these also accept the rest of what a source can carry.
//
// Property-change records. Accepting one keeps the current properties, so the record
// (which holds the OLD properties) is simply dropped.
const PROP_CHANGE_TAGS = ['pPrChange', 'rPrChange', 'sectPrChange', 'tblPrChange', 'tblPrExChange',
  'trPrChange', 'tcPrChange', 'tblGridChange', 'numberingChange'];
// Native-move range markers: once the move is accepted they mark nothing.
const MOVE_RANGE_TAGS = ['moveFromRangeStart', 'moveFromRangeEnd', 'moveToRangeStart', 'moveToRangeEnd'];
const INCOMING_REV_RE = new RegExp(`<w:(?:ins|del|moveFrom|moveTo|${PROP_CHANGE_TAGS.join('|')}|${MOVE_RANGE_TAGS.join('|')})\\b`);

/**
 * Accept every tracked change in a paragraph, table row or block: content revisions as
 * flattenAcceptRevisions does, plus property changes and move ranges. Paragraph-mark
 * markers (<w:ins/>/<w:del/> in the pPr's rPr) are dropped either way, so the paragraph
 * keeps its own mark. For an inserted mark that IS the accepted state. For a deleted
 * mark it is not (Word would join the paragraph to the next), but the parser keeps
 * every <w:p> as its own paragraph, so the diff compared it as one: dropping the
 * marker keeps the output's structure the one Fredline compared. Returns the input
 * unchanged when there is nothing to accept.
 */
export function acceptIncomingRevisions(xml: string): string {
  if (!xml || !INCOMING_REV_RE.test(xml)) return xml;
  const root = parseEl(xml);
  acceptInsDelIn(root);
  for (const tag of [...PROP_CHANGE_TAGS, ...MOVE_RANGE_TAGS]) {
    for (const el of wEls(root, tag)) if (el.parentNode) el.parentNode.removeChild(el);
  }
  return serializeClean(root);
}

/**
 * Accept every tracked change in a properties fragment (a pPr, rPr, sectPr, tblPr +
 * tblGrid, trPr or tcPr string): drop the property-change records and the self-closing
 * revision markers (paragraph mark, table row, numbering). These fragments hold no
 * runs, so there is no content to keep. A paragraph-mark rPr left empty is removed, so
 * that a pPr that differed from the other draft's only by the marker compares equal.
 */
export function acceptIncomingProps(xml: string): string {
  if (!xml || !INCOMING_REV_RE.test(xml)) return xml;
  let out = stripChangeChildren(xml, [...PROP_CHANGE_TAGS, 'ins', 'del', 'moveFrom', 'moveTo']);
  out = out.replace(new RegExp(`<w:(?:${MOVE_RANGE_TAGS.join('|')})\\b[^>]*/>`, 'g'), '');
  return out.replace(/<w:rPr(\s[^>]*[^/])?>\s*<\/w:rPr>/g, '');
}

/**
 * A copy of one side of a diff paragraph (a body paragraph, block or table row) with
 * every incoming tracked change accepted in its XML: rawXml, pPr, its tokens' run
 * formatting, and for a table row its row/cell/table properties and cells. Its text is
 * already the accepted text (the parser reads it that way). Returns the same object
 * when there is nothing to accept, and never modifies its argument (the preview and a
 * later Combine run use the same parsed data).
 */
export function acceptIncomingInRich(rich: RichParagraph | undefined): RichParagraph | undefined {
  if (!rich) return rich;
  const has = (s: string | null | undefined) => !!s && INCOMING_REV_RE.test(s);
  if (!has(rich.rawXml) && !has(rich.pPrXml) && !has(rich.tablePropsXml)) return rich;
  const tok = (t: RichToken): RichToken => {
    if (!has(t.rPrXml) && !t.segments?.some(s => has(s.rPrXml))) return t;
    const out: RichToken = { ...t, rPrXml: acceptIncomingProps(t.rPrXml) };
    if (t.segments) out.segments = t.segments.map(s => ({ ...s, rPrXml: acceptIncomingProps(s.rPrXml) }));
    return out;
  };
  const out: RichParagraph = {
    ...rich,
    rawXml: rich.rawXml ? acceptIncomingRevisions(rich.rawXml) : rich.rawXml,
    pPrXml: acceptIncomingProps(rich.pPrXml),
    richTokens: rich.richTokens.map(tok),
  };
  // a footnote mark's or picture's run can carry a tracked format change too (a link's or
  // content control's opening tag is not a whole element, and holds none to accept)
  if (rich.inline?.some(ev => (ev.kind === 'run' || ev.kind === 'mark') && has(ev.xml))) {
    out.inline = rich.inline.map(ev => (ev.kind === 'run' || ev.kind === 'mark') && has(ev.xml) ? { ...ev, xml: acceptIncomingRevisions(ev.xml) } : ev);
  }
  if (rich.tablePropsXml) out.tablePropsXml = acceptIncomingProps(rich.tablePropsXml);
  if (rich.cellData) {
    out.cellData = {
      trPrXml: acceptIncomingProps(rich.cellData.trPrXml),
      cells: rich.cellData.cells.map(c => ({
        ...c,
        tcPrXml: acceptIncomingProps(c.tcPrXml),
        rawXml: acceptIncomingRevisions(c.rawXml),
        richTokens: c.richTokens.map(tok),
        paragraphs: c.paragraphs.map(cp => acceptIncomingInRich(cp)!),
      })),
    };
  }
  return out;
}

/** True if a pPr's paragraph-mark rPr carries a tracked deletion of the mark. */
function markDeleted(pPrXml: string | undefined): boolean {
  const rPr = stripChangeChildren(pPrXml || '').match(/<w:rPr\b[^>]*>([\s\S]*?)<\/w:rPr>/);
  return !!rPr && /<w:del\b/.test(rPr[1]);
}

/**
 * acceptIncomingInRich on both sides of a diff paragraph (same object if neither changes).
 * One case is not a plain accept: a REBUILT paragraph whose revised mark carries an
 * incoming deletion, where the original's paragraph keeps its mark. That is a paragraph
 * join the revised draft made. The rebuilt paragraph used to copy the marker under its
 * author; it now becomes Fredline's own paragraph-mark deletion (paraMark), so Accept
 * All still joins the paragraphs and Reject All still keeps them apart. The same for an
 * unchanged paragraph (same words) whose mark the draft deleted: without this, its incoming
 * marker was dropped with the rest of the draft's revisions and the join was lost.
 */
export function acceptIncomingInPara(para: DiffParagraph): DiffParagraph {
  const origRich = acceptIncomingInRich(para.origRich), revRich = acceptIncomingInRich(para.revRich);
  const join = (para.type === 'modify' || para.type === 'equal') && !para.paraMark && !!para.origRich && !!para.revRich
    && markDeleted(para.revRich.pPrXml) && !markDeleted(para.origRich.pPrXml);
  if (join) return { ...para, origRich, revRich, paraMark: 'deleted' };
  return (origRich === para.origRich && revRich === para.revRich) ? para : { ...para, origRich, revRich };
}

export function applyRevisionToAllRuns(rawXml: string, markupFn: (rPrXml: string) => string, tcType: string): string {
  // Accept any pre-existing tracked changes first, so re-marking the whole
  // paragraph can't produce nested <w:ins>/<w:del> revisions.
  rawXml = flattenAcceptRevisions(rawXml);

  if (!_useTrackChanges) {
    // Markup mode: whole-paragraph delete/insert/move — add a margin change bar
    // (no-ops on a <w:tr> passed for a whole-row change).
    return addChangeBarToParagraph(applyMarkupToAllRuns(rawXml, markupFn));
  }
  return applyRevisionToAllRuns_node(rawXml, tcType);
}

/** True if `el` has a `w:r` ancestor — i.e. it lives inside another run's content
 * (a text box in a drawing group, a ruby text, etc.). */
function hasRunAncestor(el: Element): boolean {
  for (let n: Node | null = el.parentNode; n && (n as Element).nodeType === 1; n = n.parentNode) {
    const e = n as Element;
    if (e.localName === 'r' && e.namespaceURI === W_NS) return true;
  }
  return false;
}

/** The nearest `w:r` ancestor of `el` (or null). */
function nearestRun(el: Element): Element | null {
  for (let n: Node | null = el.parentNode; n && (n as Element).nodeType === 1; n = n.parentNode) {
    const e = n as Element;
    if (e.localName === 'r' && e.namespaceURI === W_NS) return e;
  }
  return null;
}

function applyRevisionToAllRuns_node(rawXml: string, tcType: string): string {
  const p = parseEl(rawXml);
  const wrapper = (tcType === 'delete' || tcType === 'move-from') ? 'w:del' : 'w:ins';
  const useDelText = (tcType === 'delete' || tcType === 'move-from');
  const made = new Set<Element>();
  // Wrap every TOP-LEVEL run, in place (handles runs inside hyperlinks/smartTags too).
  // Runs nested inside another run's content (text boxes in drawing groups) are
  // skipped: wrapping them too puts a <w:del> inside a <w:del> — invalid OOXML that
  // makes Word report unreadable content. Word itself only marks the anchor run.
  for (const run of wEls(p, 'r')) {
    if (!run.parentNode) continue;
    if (hasRunAncestor(run)) continue;
    // The wrapped run becomes part of a Fredline revision construct — normalize
    // its rPr child order (source docs often carry misordered run properties).
    const rPrEl = directChild(run, 'rPr');
    if (rPrEl) {
      const kids = Array.from(rPrEl.childNodes).filter(c => (c as Element).nodeType === 1) as Element[];
      if (kids.length > 1) {
        const sorted = kids.map((el, i) => ({ el, i }))
          .sort((a, b) => (rPrIdx(a.el.localName) - rPrIdx(b.el.localName)) || (a.i - b.i));
        if (sorted.some((k, i) => k.el !== kids[i])) for (const { el } of sorted) rPrEl.appendChild(el);
      }
    }
    if (useDelText) {
      // Only rename text nodes that belong DIRECTLY to this run — text inside a
      // nested text box keeps <w:t> (delText outside a del run context is invalid).
      for (const t of wEls(run, 't')) if (nearestRun(t) === run) renameEl(t, 'w:delText');
      for (const it of wEls(run, 'instrText')) if (nearestRun(it) === run) renameEl(it, 'w:delInstrText');
    }
    const w = makeEl(p.ownerDocument!, wrapper);
    w.setAttribute('w:id', String(getNextRevId()));
    w.setAttribute('w:author', _tcAuthor);
    w.setAttribute('w:date', _tcDate);
    run.parentNode.insertBefore(w, run);
    w.appendChild(run);
    made.add(w);
  }
  // The block's bookmarks and comment ranges go with its text: into the revision beside them
  // (a lone one of their own if none), so Accept All drops a deleted paragraph's bookmarks and
  // Reject All an inserted one's. Left between the runs, a deleted heading's bookmark survived
  // Accept All on the paragraph after it, beside the revised heading's own.
  const sibling = (el: Element, dir: 'previousSibling' | 'nextSibling'): Element | null => {
    let n = el[dir];
    while (n && n.nodeType !== 1) n = n[dir];
    return n as Element | null;
  };
  for (const name of ['bookmarkStart', 'bookmarkEnd', 'commentRangeStart', 'commentRangeEnd']) {
    for (const mk of wEls(p, name)) {
      if (!mk.parentNode || hasRunAncestor(mk)) continue;
      let inRev = false;
      for (let a = mk.parentNode as Element | null; a && a.nodeType === 1; a = a.parentNode as Element | null) {
        if (a.namespaceURI === W_NS && /^(ins|del|moveFrom|moveTo)$/.test(a.localName)) { inRev = true; break; }
      }
      if (inRev) continue;
      const before = sibling(mk, 'previousSibling'), after = sibling(mk, 'nextSibling');
      if (before && made.has(before)) before.appendChild(mk);
      else if (after && made.has(after)) after.insertBefore(mk, after.firstChild);
      else {
        const w = makeEl(p.ownerDocument!, wrapper);
        w.setAttribute('w:id', String(getNextRevId()));
        w.setAttribute('w:author', _tcAuthor);
        w.setAttribute('w:date', _tcDate);
        mk.parentNode.insertBefore(w, mk);
        w.appendChild(mk);
        made.add(w);
      }
    }
  }
  return serializeClean(p);
}

/**
 * Normalize rPrXml for comparison: extract the inner content of <w:rPr>...</w:rPr>,
 * stripping namespace re-declarations, to determine if formatting actually changed.
 */
function normalizeRPr(rPrXml: string): string {
  if (!rPrXml) return '';
  // Extract inner content of w:rPr
  const match = rPrXml.match(/<w:rPr[^>]*>([\s\S]*?)<\/w:rPr>/);
  if (!match) return '';
  // Strip namespace declarations that serializeNode adds
  return match[1]
    .replace(/\s*xmlns:\w+="[^"]*"/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Letters of the complex scripts: Hebrew, Arabic, Syriac, Thaana, N'Ko, the Indic scripts,
// Thai, Lao, Tibetan, Myanmar, Khmer, and the Hebrew and Arabic presentation forms.
const COMPLEX_SCRIPT = /[\u0590-\u08FF\u0900-\u0DFF\u0E00-\u0FFF\u1000-\u109F\u1780-\u17FF\uFB1D-\uFDFF\uFE70-\uFEFF]/;

/**
 * Run properties as they show on `text`, for deciding whether its formatting changed. Word keeps
 * separate bold, italic, size and font settings for complex scripts (w:bCs, w:iCs, w:szCs,
 * rFonts w:cs): on text with none of those letters they change nothing a reader can see, so a
 * difference only there is not a format change (it was tracked as one: "Formatted: Complex
 * Script Bold" on English text). Kept when the text has such letters or the run is marked
 * complex script or right-to-left (w:cs, w:rtl), where those settings are the ones that show.
 */
// Hangul, CJK, kana, full- and half-width forms: the text a run's East Asian font applies to
const EAST_ASIAN = /[\u1100-\u11FF\u2E80-\u9FFF\uA960-\uA97F\uAC00-\uD7FF\uF900-\uFAFF\uFE30-\uFE4F\uFF00-\uFFEF]/;

function formatKey(rPrXml: string, text: string): string {
  let k = normalizeRPr(rPrXml);
  // the East Asian font and language apply only to East Asian characters: in other text a
  // difference there is not a change anyone sees (as with the complex-script ones below)
  if (k && !EAST_ASIAN.test(text)) {
    k = k.replace(/(<w:rFonts\b[^>]*?)\s+w:(?:eastAsia|eastAsiaTheme)="[^"]*"/g, '$1')
      .replace(/(<w:rFonts\b[^>]*?)\s+w:(?:eastAsia|eastAsiaTheme)="[^"]*"/g, '$1')
      .replace(/(<w:lang\b[^>]*?)\s+w:eastAsia="[^"]*"/g, '$1')
      .replace(/<w:(?:rFonts|lang)\s*\/>/g, '');
  }
  if (!k || COMPLEX_SCRIPT.test(text) || /<w:(?:cs|rtl)\b(?![^>]*\bw:val="(?:0|false|off)")/.test(k)) return k;
  return k
    .replace(/<w:(bCs|iCs|szCs)\b[^>]*?(?:\/>|>[\s\S]*?<\/w:\1>)/g, '')
    .replace(/(<w:rFonts\b[^>]*?)\s+w:(?:cs|cstheme)="[^"]*"/g, '$1')
    .replace(/(<w:rFonts\b[^>]*?)\s+w:(?:cs|cstheme)="[^"]*"/g, '$1')
    .replace(/(<w:lang\b[^>]*?)\s+w:bidi="[^"]*"/g, '$1')
    .replace(/<w:(?:rFonts|lang)\s*\/>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Normalize pPrXml for comparison: extract inner content of <w:pPr>,
 * stripping namespace re-declarations.
 */
function normalizePPr(pPrXml: string): string {
  if (!pPrXml) return '';
  const match = pPrXml.match(/<w:pPr[^>]*>([\s\S]*?)<\/w:pPr>/);
  if (!match) return '';
  return match[1]
    .replace(/\s*xmlns:\w+="[^"]*"/g, '')
    // not formatting: editing-session ids, and a section's header/footer relationship ids,
    // which each package numbers its own way (the headers' content is compared separately)
    .replace(/\s+w:rsid\w*="[^"]*"/g, '')
    .replace(/(<w:(?:header|footer)Reference\b[^>]*?)\s+r:id="[^"]*"/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Build a pPrChange element to track paragraph property changes.
 * Returns the revised pPr with pPrChange appended inside it.
 */
function buildPPrWithChange(newPPrXml: string, oldPPrXml: string): string {
  // If the pPr already records a tracked paragraph-format change, keep it — OOXML
  // allows only one pPrChange per pPr, and injecting into the existing one's
  // nested pPr corrupts the XML.
  if (newPPrXml && /<w:pPrChange\b/.test(newPPrXml)) return newPPrXml;
  return buildPPrWithChange_node(newPPrXml, oldPPrXml);
}

function buildPPrWithChange_node(newPPrXml: string, oldPPrXml: string): string {
  const pPr = parseEl(newPPrXml || '<w:pPr/>');
  const change = makeEl(pPr.ownerDocument!, 'w:pPrChange');
  setRevAttrs(change);
  change.appendChild(importEl(pPr.ownerDocument!, oldPPrXml ? stripToPPrBase(oldPPrXml) : '<w:pPr/>'));
  pPr.appendChild(change); // last child — correct position by construction
  return serializeClean(pPr);
}

/**
 * Extract footnote/endnote reference runs from paragraph rawXml.
 * These runs contain <w:footnoteReference> or <w:endnoteReference> elements
 * that must be preserved in the output to avoid "unreadable content" errors.
 */
export function extractNoteReferenceRuns(rawXml: string | null): string[] {
  if (!rawXml) return [];
  // the paragraph's own runs that carry a footnote/endnote reference (a reference inside a text
  // box stays with the text box, which extractEmbeddedRuns keeps whole)
  return topLevelRuns(rawXml).filter(r => !EMBED_RE.test(r) && /<w:(?:footnoteReference|endnoteReference)\b/.test(r));
}

const EMBED_RE = /<(?:w:drawing|w:pict|w:object|mc:AlternateContent)\b/;
/**
 * The paragraph's own runs, each as its whole element: not the runs inside another run (a text
 * box's content sits inside the run that holds the shape). A lazy `<w:r…>…</w:r>` match stopped
 * at the first `</w:r>` inside such a text box, so the text box run was cut in half and the
 * redline's document.xml was not well-formed.
 */
export function topLevelRuns(xml: string): string[] {
  const out: string[] = [];
  const re = /<(\/?)w:r(?=[\s>\/])[^>]*?(\/?)>/g;
  let depth = 0, start = -1, m: RegExpExecArray | null;
  while ((m = re.exec(xml)) !== null) {
    if (m[2]) { if (depth === 0) out.push(m[0]); continue; }   // an empty <w:r/>
    if (!m[1]) { if (depth === 0) start = m.index; depth++; continue; }
    if (depth > 0) depth--;
    if (depth === 0 && start >= 0) { out.push(xml.slice(start, re.lastIndex)); start = -1; }
  }
  return out;
}

/**
 * Extract runs that carry embedded non-text content — drawings/charts/images
 * (<w:drawing>), legacy shapes/text boxes (<w:pict>), OLE objects (<w:object>),
 * and equations (<m:oMath>/<m:oMathPara>). When a paragraph that contains a chart
 * or image is rebuilt from word tokens, these runs are otherwise dropped — the
 * chart vanishes and its relationship is orphaned. Re-appending the original runs
 * keeps the content and its r:embed/r:id reference intact (the media/chart parts
 * themselves are already copied into the output package).
 */
export function extractEmbeddedRuns(rawXml: string | null): string {
  if (!rawXml) return '';
  let out = '';
  // Runs containing embedded content (drawings/charts/images/shapes/OLE), whole (a text box's
  // own paragraphs and runs included).
  // (equations inside such a run, in a text box, go with it: not scanned again below)
  let mathSrc = rawXml;
  for (const r of topLevelRuns(rawXml)) if (EMBED_RE.test(r)) { out += r; mathSrc = mathSrc.replace(r, ''); }
  // Math: capture block-level <m:oMathPara> first, then any remaining inline
  // <m:oMath> (these are siblings of runs, not inside <w:r>). Strip the captured
  // oMathPara regions before the inline scan so nested oMath isn't added twice.
  for (const mp of mathSrc.matchAll(/<m:oMathPara\b[\s\S]*?<\/m:oMathPara>/g)) out += mp[0];
  mathSrc = mathSrc.replace(/<m:oMathPara\b[\s\S]*?<\/m:oMathPara>/g, '');
  for (const mo of mathSrc.matchAll(/<m:oMath\b[\s\S]*?<\/m:oMath>/g)) out += mo[0];
  return out;
}

/**
 * Extract comment anchors (commentRangeStart / commentRangeEnd and the run that
 * carries commentReference) from a paragraph's rawXml. When a commented paragraph
 * is rebuilt from word tokens these markers are otherwise lost, which both drops
 * the comment AND can leave comments.xml orphaned or comment ranges unbalanced
 * (split across an edited/unedited boundary) → Word "unreadable content" error.
 * Re-appending the matched start+end+reference for each id keeps every range
 * balanced and the comment attached to the paragraph.
 */
export function extractCommentMarkers(rawXml: string | null): string {
  if (!rawXml) return '';
  let starts = '', ends = '', refRuns = '';
  // the paragraph's own anchors: not those inside a text box (they stay in its run)
  const re = /<(\/?)w:r(?=[\s>\/])[^>]*?(\/?)>|<w:commentRange(Start|End)\b[^>]*\/>/g;
  let depth = 0, m: RegExpExecArray | null;
  while ((m = re.exec(rawXml)) !== null) {
    if (m[3]) { if (depth === 0) { if (m[3] === 'Start') starts += m[0]; else ends += m[0]; } continue; }
    if (m[2]) continue;
    if (!m[1]) depth++; else if (depth > 0) depth--;
  }
  for (const r of topLevelRuns(rawXml)) if (!EMBED_RE.test(r) && /<w:commentReference\b/.test(r)) refRuns += r;
  // Order: range starts, range ends, then the reference runs (valid OOXML ordering).
  return starts + ends + refRuns;
}

/**
 * Split an entry's text into per-format-run pieces using the token's segments, so a
 * word that spans multiple source runs (e.g. a bold defined term + a non-bold `")`)
 * emits one run per piece instead of forcing the whole word into one rPr. The leading
 * inter-word space rides on the first piece. A single segment is still meaningful (a
 * sub-word piece lying inside one run that isn't the word's primary run). Returns null
 * when there are no segments or they don't line up with `text`, so the caller falls
 * back to its single-run path.
 */
export function splitEntryBySegments(text: string, segments?: { text: string; rPrXml: string }[]): { text: string; rPr: string }[] | null {
  if (!segments || segments.length < 1) return null;
  const wordLen = segments.reduce((n, s) => n + s.text.length, 0);
  if (wordLen > text.length) return null;
  const prefix = text.slice(0, text.length - wordLen);
  if (text.slice(text.length - wordLen) !== segments.map(s => s.text).join('')) return null;
  return segments.map((s, i) => ({ text: (i === 0 ? prefix : '') + s.text, rPr: s.rPrXml }));
}

export function buildModifiedParagraph(para: { diff: DiffEntry[]; origRich: any; revRich: any }, opts: { spacing?: boolean } = {}): string {
  const { diff, origRich, revRich } = para;
  let pPrXml = revRich ? revRich.pPrXml : (origRich ? origRich.pPrXml : '');

  // Track paragraph property changes (spacing, justification, etc.)
  if (_useTrackChanges && origRich && revRich) {
    const origPPrNorm = normalizePPr(origRich.pPrXml);
    const revPPrNorm = normalizePPr(revRich.pPrXml);
    if (origPPrNorm !== revPPrNorm) {
      recChange('f', { type: 'format', scope: 'format' });
      // (also for the record alone: it gives no id when the draft already tracks the change, Combine)
      pPrXml = buildPPrWithChange(revRich.pPrXml, origRich.pPrXml);
    }
  }

  const runs = diffRunsXml(diff, origRich, revRich, { moveTo: (para as any).type === 'move-to', spacing: opts.spacing !== false });
  if (_recordOnly) return `<w:p>${pPrXml}</w:p>`;
  // footnote marks, comments, pictures, bookmarks, links and fields are back where they were
  // (inline.ts): the parser recorded where
  if (revRich?.inline || origRich?.inline) return finishModified(pPrXml, runs);
  return finishModified(pPrXml, runs + appendedAnchors(origRich, revRich));
}

function finishModified(pPrXml: string, runs: string): string {
  const built = `<w:p>${pPrXml}${runs}</w:p>`;
  // Markup mode has no revisions for Word to draw change bars from, so add one
  // ourselves for an edited paragraph. Track-changes mode gets it from Word.
  return _useTrackChanges ? built : addChangeBarToParagraph(built);
}

/**
 * For a side whose events the parser did not record (a table cell compared as one paragraph):
 * what the word rebuild would otherwise drop, added at the end.
 */
export function appendedAnchors(origRich: { rawXml?: string | null } | null | undefined, revRich: { rawXml?: string | null } | null | undefined): string {
  let runs = '';
  const revRaw = revRich?.rawXml ?? null, origRaw = origRich?.rawXml ?? null;
  // Preserve footnote/endnote references from source paragraphs.
  // These are in standalone runs (no text) that buildRun() can't reconstruct.
  // Without them, footnotes.xml has orphaned entries → Word "unreadable content" error.
  const revNoteRuns = extractNoteReferenceRuns(revRaw);
  for (const noteRun of revNoteRuns) {
    runs += noteRun;
  }

  // Preserve comment anchors so comments survive and ranges stay balanced.
  // Prefer the revised side; fall back to the original if only it was commented.
  const commentMarkers = extractCommentMarkers(revRaw) || extractCommentMarkers(origRaw);
  if (commentMarkers) runs += commentMarkers;

  // Preserve embedded charts/images/objects/equations from the source paragraph
  // (revised side preferred). Otherwise editing text in the same paragraph as a
  // chart would silently delete the chart.
  const embeddedRev = extractEmbeddedRuns(revRaw);
  const embeddedOrig = embeddedRev ? null : extractEmbeddedRuns(origRaw);
  const embedded = embeddedRev || (embeddedOrig && tagCrossRelIds(embeddedOrig));
  if (embedded) runs += embedded;
  return runs;
}

/**
 * The runs of a paragraph's word diff, the one writer for edited text in the .docx: body
 * paragraphs, table cells and footnotes. Each piece keeps the source's whitespace before it
 * (`pieces.ts`: tabs, line breaks, double and no-break spaces) and its own word's per-run
 * formatting; consecutive changes of one kind become one revision (track changes) or one
 * run each (markup); an unchanged word whose formatting changed records an rPrChange per run.
 * What is not a word (footnote marks, comments, pictures, bookmarks, links, fields) goes back
 * where it was (inline.ts) when the parser recorded where (RichParagraph.inline).
 * `moveTo`: the paragraph is a moved-and-edited destination (its deletions nest in w:ins).
 * Each change is recorded as it is written (change-record.ts): a run of consecutive changed
 * words of one kind (and move) is one change, keyed `keyPrefix` + 'r<k>' ('mt' for a moved
 * paragraph's moved words, 'e<k>' for a mark or object changed on its own, 's<k>' for spacing);
 * `cell` for a cell's.
 * `spacing` (tracked output, the body and table cells): an unchanged word whose whitespace before
 * it differs between the drafts in its tabs or line breaks, or that only one draft has (pieces.ts
 * gapsDiffer), has that whitespace tracked, the original's deleted and the revised draft's
 * inserted, so Reject All gives the original's layout back. Runs of spaces are not tracked (the
 * revised draft's are written).
 */
export function diffRunsXml(diff: DiffEntry[], origRich: any, revRich: any, opts: { moveTo?: boolean; keyPrefix?: string; cell?: number; spacing?: boolean } = {}): string {
  const origTokens = origRich ? origRich.richTokens || [] : [];
  const revTokens = revRich ? revRich.richTokens || [] : [];

  // Phase 1: Build an array of run entries with metadata
  interface RunEntry {
    type: string;
    text: string;
    value: string;
    rPr: string;
    /** the piece it is written from (its change: runKey) */
    piece?: number;
    /** whitespace alone, tracked because the drafts differ in it (gapsDiffer); where it is on its side */
    spacing?: { fromOrig: boolean; start: number; end: number };
    origRPr?: string;
    segments?: { text: string; rPrXml: string }[];
    // Equal words only: the ORIGINAL side's per-run formatting of the same text, so a
    // tracked format change can be recorded per run rather than per whole word.
    origSegments?: { text: string; rPrXml: string }[];
  }
  // A token's per-run segments clipped to this diff piece (whole word, or a sub-word
  // piece from intra-word refinement located by its character offset).
  const segOf = (tok: any, value: string, charIdx?: number) =>
    pieceSegments(tok, value, charIdx) || undefined;

  // An event (footnote mark, bookmark, link start …: inline.ts) between the pieces: `status` as
  // placed (equal: written as it is), `type` the change it is written with.
  interface EventEntry { event: true; type: string; status: PlacedEvent['status']; xml: string; fromOrig: boolean; kind: InlineEvent['kind'] }
  const entries: (RunEntry | EventEntry)[] = [];
  const revSide = sideOf(revRich), origSide = sideOf(origRich);
  // where each word starts in its paragraph's text (for the change record)
  const offsets = (side: { tokens: RichToken[]; gaps: string[] | null }): number[] | null => {
    if (!side.gaps) return null;
    const o: number[] = [];
    let at = 0;
    side.tokens.forEach((t, k) => { at += side.gaps![k].length; o.push(at); at += (t.word || '').length; });
    return o;
  };
  const origOff = offsets(origSide), revOff = offsets(revSide);
  const trackSpacing = !!opts.spacing && _useTrackChanges;
  /** An unchanged piece's whitespace that differs in its tabs or line breaks, or that only one
   *  draft has (gapsDiffer): tracked, the original's deleted and the revised draft's inserted (in
   *  the words' formatting). */
  const spacingDone = new Set<number>();
  const pushSpacing = (pc: Piece, piece: number): boolean => {
    const d = pc.entry;
    if (!trackSpacing || d.type !== 'equal' || spacingDone.has(piece) || pc.origGap == null || !gapsDiffer(pc.origGap, pc.gap)) return false;
    spacingDone.add(piece);
    const oi = wordOf(d, 'orig'), ri = wordOf(d, 'rev');
    const oAt = origOff?.[oi] ?? 0, rAt = revOff?.[ri] ?? 0;
    // (in the word's formatting, without a formatting revision the draft tracked on it: Combine)
    const plain = (rPr: string | undefined) => stripChangeChildren(rPr || '', ['rPrChange']);
    if (pc.origGap) entries.push({ type: 'delete', text: pc.origGap, value: '', rPr: plain(origTokens[oi]?.rPrXml), piece, spacing: { fromOrig: true, start: oAt - pc.origGap.length, end: oAt } });
    if (pc.gap) entries.push({ type: 'insert', text: pc.gap, value: '', rPr: plain(revTokens[ri]?.rPrXml), piece, spacing: { fromOrig: false, start: rAt - pc.gap.length, end: rAt } });
    return true;
  };
  const pieces = diffPieces(diff, origSide, revSide, { visual: !_useTrackChanges });
  const placed = placeInline(pieces,
    origRich ? { inline: origRich.inline, words: origTokens.length } : null,
    revRich ? { inline: revRich.inline, words: revTokens.length } : null) || [];
  // a moved paragraph's destination is all inserted: its marks and bookmarks too (unmarked, Reject
  // All kept them there as well as where the paragraph came from); a link's start and end cannot be
  if (opts.moveTo) for (const p of placed) if (p.status === 'equal' && (p.ev.kind === 'run' || p.ev.kind === 'mark')) p.status = 'insert';
  let pe = 0;
  /** the events placed before `loc` (cmpLoc), in order */
  const eventsBefore = (loc: Loc, inclusive = false) => {
    while (pe < placed.length && (inclusive ? cmpLoc(placed[pe].loc, loc) <= 0 : cmpLoc(placed[pe].loc, loc) < 0)) {
      const p = placed[pe++];
      entries.push({ event: true, type: p.status, status: p.status, xml: p.ev.xml, fromOrig: p.fromOrig, kind: p.ev.kind });
    }
  };

  // One piece's text from character `a` to `b` (its whitespace in front when a = 0 and `gap`).
  // (a piece without a word index is formatted like the side's first word: see stage 6 of the cleanup plan)
  const pushText = (pc: Piece, a: number, b: number, gap: string, piece: number) => {
    const d = pc.entry;
    if (a === 0 && pushSpacing(pc, piece)) gap = '';
    const value = d.value.slice(a, b);
    const text = gap + value;
    const at = (ci: number | undefined) => (ci != null ? ci + a : (a ? a : undefined));
    if (d.type === 'equal') {
      const revToken = revTokens[wordOf(d, 'rev')];
      const revRPr = revToken ? revToken.rPrXml : '';
      const origToken = origTokens[wordOf(d, 'orig')];
      const origRPr = origToken ? origToken.rPrXml : '';
      entries.push({ type: 'equal', text, value, rPr: revRPr, origRPr, piece,
        segments: segOf(revToken, value, at(d.revCharIdx)),
        origSegments: origToken ? (pieceSegments(origToken, value, at(d.origCharIdx)) || [{ text: value, rPrXml: origRPr }]) : undefined });
    } else if (d.type === 'delete' || d.type === 'move-from') {
      const token = origTokens[wordOf(d, 'orig')];
      entries.push({ type: d.type, text, value, rPr: token ? token.rPrXml : '', segments: segOf(token, value, at(d.origCharIdx)), piece });
    } else {
      const token = revTokens[wordOf(d, 'rev')];
      entries.push({ type: d.type, text, value, rPr: token ? token.rPrXml : '', segments: segOf(token, value, at(d.revCharIdx)), piece });
    }
  };

  // An event between a change's whitespace and its words (a field's start before its changed
  // result, "See Section ⟦REF⟧[-2.1-]{+2.2+}"), when the change's other side has the same
  // whitespace in front: that whitespace is unchanged, so it is written once, before both, and the
  // event follows it in both drafts. (Kept with each side, Accept All read "Section⟦REF⟧ 2.2":
  // the space inside the field's result, lost when Word updates the field.)
  const isOrigChange = (t: string) => t === 'delete' || t === 'move-from';
  const isRevChange = (t: string) => t === 'insert' || t === 'move-to';
  const gapless = new Set<number>(), sharedGap = new Map<number, { gap: string; rPr: string }>();
  for (const pl of placed) {
    const i = pl.loc.i, pc = pieces[i];
    if (pl.loc.k !== 0 || !pc || pc.entry.type === 'equal' || !pc.gap || gapless.has(i)) continue;
    // the other side's piece, with nothing of this side's between them (the whitespace moves to
    // before the earlier one, so this side's text between would lose it): the next inserted piece
    // after a run of deleted ones, or the deleted piece just before an inserted one
    let j = -1;
    if (isOrigChange(pc.entry.type)) {
      let x = i + 1;
      while (x < pieces.length && isOrigChange(pieces[x].entry.type)) x++;
      if (x < pieces.length && isRevChange(pieces[x].entry.type)) j = x;
    } else if (i > 0 && isOrigChange(pieces[i - 1].entry.type)) j = i - 1;
    if (j < 0 || gapless.has(j) || pieces[j].gap !== pc.gap) continue;
    const revPc = isRevChange(pc.entry.type) ? pc : pieces[j];
    const tok = revTokens[wordOf(revPc.entry, 'rev')];
    sharedGap.set(Math.min(i, j), { gap: pc.gap, rPr: tok ? tok.rPrXml : '' });
    gapless.add(i); gapless.add(j);
  }

  pieces.forEach((pc, i) => {
    eventsBefore({ i, k: -1 }, true);            // right after the piece before, or before this one's whitespace
    const shared = sharedGap.get(i);
    if (shared) entries.push({ type: 'equal', text: shared.gap, value: '', rPr: shared.rPr });
    // the piece's text, cut where an event sits inside it (or between it and its whitespace)
    const len = pc.entry.value.length;
    let a = 0, gap = gapless.has(i) ? '' : pc.gap;
    while (pe < placed.length && placed[pe].loc.i === i && placed[pe].loc.k < len) {
      const k = placed[pe].loc.k;
      if (k > a || (k === 0 && gap)) {
        if (k > a) pushText(pc, a, k, gap, i);
        else if (pushSpacing(pc, i)) { /* the whitespace alone, tracked */ }
        else {
          // the whitespace alone (an event between it and the word), in the word's formatting
          const d = pc.entry, fromOrigSide = d.type === 'delete' || d.type === 'move-from';
          const tok = fromOrigSide ? origTokens[wordOf(d, 'orig')] : revTokens[wordOf(d, 'rev')];
          entries.push({ type: d.type, text: gap, value: '', rPr: tok ? tok.rPrXml : '', piece: i });
        }
        gap = ''; a = k;
      }
      eventsBefore({ i, k }, true);
    }
    pushText(pc, a, len, gap, i);
  });
  eventsBefore({ i: pieces.length, k: -1 }, true);   // after the last word
  // A tab or line break after the last word is layout a reader sees: keep it (trailing spaces are not)
  const tail = trailingGap(revSide);
  const origTail = origSide.gaps ? origSide.gaps[origSide.gaps.length - 1] || '' : '';
  if (trackSpacing && entries.length && layoutOf(origTail) !== layoutOf(tail)) {
    // (tracked when the drafts' differ: the original's deleted, the revised draft's inserted)
    const end = (off: number[] | null, toks: RichToken[]) => (off && toks.length ? off[toks.length - 1] + (toks[toks.length - 1].word || '').length : 0);
    const plain = (rPr: string | undefined) => stripChangeChildren(rPr || '', ['rPrChange']);
    if (layoutOf(origTail) && origTokens.length) { const at = end(origOff, origTokens); entries.push({ type: 'delete', text: origTail, value: '', rPr: plain(origTokens[origTokens.length - 1].rPrXml), spacing: { fromOrig: true, start: at, end: at + origTail.length } }); }
    if (layoutOf(tail) && revTokens.length) { const at = end(revOff, revTokens); entries.push({ type: 'insert', text: tail, value: '', rPr: plain(revTokens[revTokens.length - 1].rPrXml), spacing: { fromOrig: false, start: at, end: at + tail.length } }); }
  } else if (/[\t\n\f\v]/.test(tail) && entries.length && revTokens.length) {
    const rPr = revTokens[revTokens.length - 1].rPrXml || '';
    entries.push({ type: 'equal', text: tail, value: tail, rPr });
  }
  eventsBefore({ i: pieces.length + 1, k: -1 });     // after everything

  // An event that is a change joins the change it sits in or beside (so a footnote mark deleted
  // with its word is in that word's deletion): its kind (deleted / inserted) is that change's.
  const sameKind = (status: string, type: string) =>
    status === 'delete' ? (type === 'delete' || type === 'move-from') : (type === 'insert' || type === 'move-to');
  for (const order of [1, -1]) {
    for (let x = order > 0 ? 0 : entries.length - 1; x >= 0 && x < entries.length; x += order) {
      const e = entries[x], nb = entries[x - order];
      if ('event' in e && e.status !== 'equal' && e.type === e.status && nb && sameKind(e.status, nb.type)) e.type = nb.type;
    }
  }

  // The changes, as the change list and the summary count them: a run of consecutive changed
  // pieces of one kind and move is one change (an unchanged piece ends it); a moved-and-edited
  // paragraph's moved words are one change. Where each sits in its source paragraph, and which
  // diff entries it covers, for the change list and the review page.
  const pfx = opts.keyPrefix || '';
  const runKey: (string | null)[] = [];
  const runInfo = new Map<string, { type: RecordedType; moveId?: number; span?: [number, number]; entries: number[]; chars: string }>();
  {
    const entryIdx = new Map<DiffEntry, number>(diff.map((d, k) => [d, k]));
    let prev: { type: string; moveId?: number; key: string } | null = null, nRun = 0;
    pieces.forEach(pc => {
      const d = pc.entry;
      if (d.type === 'equal') { prev = null; runKey.push(null); return; }
      const moveId = (d as any).moveId as number | undefined;
      let key: string;
      if (opts.moveTo && d.type === 'move-to') { key = pfx + 'mt'; prev = null; }
      else if (prev && prev.type === d.type && prev.moveId === moveId) key = prev.key;
      else { key = pfx + 'r' + nRun++; prev = { type: d.type, moveId, key }; }
      runKey.push(key);
      let info = runInfo.get(key);
      if (!info) runInfo.set(key, info = { type: d.type as RecordedType, ...(moveId != null ? { moveId } : {}), entries: [], chars: '' });
      const k = entryIdx.get(d);
      if (k != null) info.entries.push(k);
      info.chars += d.value;
      const off = pc.fromRev ? revOff : origOff, wi = pc.fromRev ? d.revWordIdx : d.origWordIdx;
      if (off && wi != null && off[wi] != null) {
        const s = off[wi] + ((pc.fromRev ? d.revCharIdx : d.origCharIdx) || 0), e = s + d.value.length;
        info.span = info.span ? [Math.min(info.span[0], s), Math.max(info.span[1], e)] : [s, e];
      }
    });
  }
  let nEvent = 0, nSpacing = 0;
  /** Record the change a group of entries is written as, and mint its ids from now on. */
  const recordGroup = (group: (RunEntry | EventEntry)[], type: string) => {
    let key: string | null = null;
    for (const ge of group) if (!('event' in ge) && !ge.spacing && ge.piece != null && runKey[ge.piece]) { key = runKey[ge.piece]; break; }
    const info = key ? runInfo.get(key) : undefined;
    if (info) { recChange(key!, { type: info.type, scope: 'words', cell: opts.cell, moveId: info.moveId, span: info.span, entries: info.entries, chars: info.chars }); return; }
    // tracked whitespace alone (a tab that became a space): a change of its own, counted apart
    const sp = group.every(ge => !('event' in ge) && ge.spacing) ? group.map(ge => (ge as RunEntry).spacing!) : null;
    if (sp) { recChange(pfx + 's' + nSpacing++, { type: type as RecordedType, scope: 'spacing', cell: opts.cell, span: [Math.min(...sp.map(x => x.start)), Math.max(...sp.map(x => x.end))], chars: '' }); return; }
    recChange(pfx + 'e' + nEvent++, { type: type as RecordedType, scope: 'words', cell: opts.cell });
  };
  const recordFormat = () => recChange('f', { type: 'format', scope: 'format' });

  if (_recordOnly) {
    // the changes the loop below records, and the ids it gives, without writing the runs
    for (let x = 0; x < entries.length;) {
      const e = entries[x];
      if ('event' in e && e.status === 'equal') { x++; continue; }
      if (!('event' in e) && e.type === 'equal') {
        if (_useTrackChanges) {
          const paired = e.origSegments ? pairSegments(e.segments || [{ text: e.value, rPrXml: e.rPr }], e.origSegments) : null;
          // (buildRunWithRPrChange gives an id unless the run already carries the draft's own change)
          const formatChange = (rPr: string) => { recordFormat(); if (!/<w:rPrChange\b/.test(rPr || '')) getNextRevId(); };
          if (paired) { for (const pc of paired) if (formatKey(pc.origRPrXml, pc.text) !== formatKey(pc.rPrXml, pc.text)) formatChange(pc.rPrXml); }
          else if (e.origRPr !== undefined && formatKey(e.origRPr, e.text) !== formatKey(e.rPr, e.text)) formatChange(e.rPr);
        }
        x++;
        continue;
      }
      let y = x + 1;
      while (y < entries.length && entries[y].type === e.type) y++;
      recordGroup(entries.slice(x, y), e.type);
      if (_useTrackChanges) {
        getNextRevId();
        if ((e.type === 'delete' || e.type === 'move-from') && opts.moveTo) { recChange(pfx + 'mt', { type: 'move-to', scope: 'words', cell: opts.cell }); getNextRevId(); }
      }
      x = y;
    }
    recSetCurrent(null);
    return '';
  }

  // Phase 2: Render entries, grouping consecutive same-type changes into single
  // revision elements (e.g. [-Hut-][-8-][-Ventures-] → [-Hut 8 Ventures-])
  let runs = '';
  let ei = 0;

  while (ei < entries.length) {
    const entry = entries[ei];

    if ('event' in entry && entry.status === 'equal') {
      // written as it is: a bookmark, a link's start or end, a mark both drafts have here
      runs += entry.xml;
      ei++;
      continue;
    }

    if (!('event' in entry) && entry.type === 'equal') {
      // Equal words: emit individually, one run per format segment. In track-changes
      // mode a run whose formatting differs from the ORIGINAL's at the same characters
      // gets an rPrChange — per run, so a bold defined term whose `")` stayed plain
      // doesn't drag the punctuation into a bold run just because the term's own
      // formatting (or formatting noise) changed between the versions.
      const prefix = entry.text.slice(0, entry.text.length - entry.value.length);
      const revSegs = entry.segments || [{ text: entry.value, rPrXml: entry.rPr }];
      const paired = (_useTrackChanges && entry.origSegments) ? pairSegments(revSegs, entry.origSegments) : null;
      if (paired) {
        paired.forEach((pc, i) => {
          const t = (i === 0 ? prefix : '') + pc.text;
          if (formatKey(pc.origRPrXml, pc.text) !== formatKey(pc.rPrXml, pc.text)) {
            recordFormat();
            runs += buildRunWithRPrChange(t, pc.rPrXml, pc.origRPrXml);
          } else runs += buildRun(t, pc.rPrXml);
        });
      } else {
        const hasRPrChange = _useTrackChanges && entry.origRPr !== undefined &&
            formatKey(entry.origRPr, entry.text) !== formatKey(entry.rPr, entry.text);
        const parts = hasRPrChange ? null : splitEntryBySegments(entry.text, entry.segments);
        if (parts) {
          for (const pt of parts) runs += buildRun(pt.text, pt.rPr);
        } else if (hasRPrChange) {
          recordFormat();
          runs += buildRunWithRPrChange(entry.text, entry.rPr, entry.origRPr!);
        } else {
          runs += buildRun(entry.text, entry.rPr);
        }
      }
      ei++;
      continue;
    }

    // Collect consecutive same-type entries for grouping
    const groupType = entry.type;
    const group = [entry];
    let ej = ei + 1;
    while (ej < entries.length && entries[ej].type === groupType) {
      group.push(entries[ej]);
      ej++;
    }

    recordGroup(group, groupType);
    if (_useTrackChanges) {
      // Track changes mode: group into a single w:del or w:ins wrapper
      const wrapper = (groupType === 'delete' || groupType === 'move-from') ? 'w:del' : 'w:ins';
      const useDelText = (groupType === 'delete' || groupType === 'move-from');
      const id = getNextRevId();
      let innerRuns = '';
      for (const ge of group) {
        if ('event' in ge) { innerRuns += trackedEventXml(ge.xml, ge.fromOrig, useDelText); continue; }
        const parts = splitEntryBySegments(ge.text, ge.segments) || [{ text: ge.text, rPr: ge.rPr || '' }];
        for (const pt of parts) innerRuns += `<w:r>${pt.rPr}${textXml(pt.text, useDelText ? 'delText' : 't')}</w:r>`;
      }
      const rev = `<${wrapper} w:id="${id}" w:author="${xmlEscape(_tcAuthor)}" w:date="${_tcDate}">${innerRuns}</${wrapper}>`;
      // In a moved-and-edited paragraph the in-transit deletions describe text that
      // ALSO lives in the move-from paragraph's deletion. A bare <w:del> here would
      // survive "reject all" as stray text at the destination (the moved block's
      // insertions vanish but its deletions get restored), duplicating the source.
      // Nesting it inside <w:ins> — valid cross-type nesting: inserted, then deleted —
      // makes reject-all drop the whole moved block and accept-all keep only the
      // surviving words, while Word still shows the strikethrough at the destination.
      const inTransitDel = wrapper === 'w:del' && !!opts.moveTo;
      // (the w:ins around it is part of the move: rejecting the move takes the text out)
      if (inTransitDel) recChange(pfx + 'mt', { type: 'move-to', scope: 'words', cell: opts.cell });
      runs += inTransitDel
        ? `<w:ins w:id="${getNextRevId()}" w:author="${xmlEscape(_tcAuthor)}" w:date="${_tcDate}">${rev}</w:ins>`
        : rev;
    } else {
      // Redline mode: each word gets its own visual markup run (split by format segments
      // so trailing/leading punctuation keeps its own formatting)
      for (const ge of group) {
        if ('event' in ge) { runs += markedEventXml(ge.xml, ge.fromOrig, groupType); continue; }
        const parts = splitEntryBySegments(ge.text, ge.segments) || [{ text: ge.text, rPr: ge.rPr || '' }];
        for (const pt of parts) runs += buildRevisionRun(pt.text, pt.rPr, groupType);
      }
    }

    ei = ej;
  }

  recSetCurrent(null);
  return runs;
}

/** An inserted or deleted footnote mark, picture, field code … inside a w:ins or w:del: a
 *  deleted run's own text and field code become w:delText and w:delInstrText (not a text box's
 *  inside it). The original's carries the original's relationship ids (tagCrossRelIds). */
function trackedEventXml(xml: string, fromOrig: boolean, deleted: boolean): string {
  if (fromOrig) xml = tagCrossRelIds(xml);
  if (!deleted || !/<w:(?:t|instrText)\b/.test(xml)) return xml;
  try {
    const el = parseEl(xml);
    for (const run of [el, ...wEls(el, 'r')].filter(r => r.localName === 'r' && !hasRunAncestor(r))) {
      for (const t of wEls(run, 't')) if (nearestRun(t) === run) renameEl(t, 'w:delText');
      for (const it of wEls(run, 'instrText')) if (nearestRun(it) === run) renameEl(it, 'w:delInstrText');
    }
    return serializeClean(el);
  } catch {
    return xml.replace(/<w:instrText\b/g, '<w:delInstrText').replace(/<\/w:instrText>/g, '</w:delInstrText>');
  }
}

/** The same in markup mode: the run drawn as the change it belongs to (a picture is shown as it is). */
function markedEventXml(xml: string, fromOrig: boolean, type: string): string {
  if (fromOrig) xml = tagCrossRelIds(xml);
  if (!/<w:r\b/.test(xml)) return xml;
  const fn = type === 'delete' ? addDeleteMarkup : type === 'move-from' ? addMoveFromMarkup : type === 'move-to' ? addMoveToMarkup : addInsertMarkup;
  return applyMarkupToAllRuns(xml, fn);
}

/**
 * For "equal" paragraphs (same text, potentially different formatting):
 * Emit the revised rawXml but inject pPrChange and/or rPrChange elements
 * to track any formatting differences. Returns null if no formatting changes
 * were found (caller should use rawXml as-is).
 */
/** True when an unchanged paragraph's formatting differs between the drafts (paragraph or run
 *  properties) — what emitEqualParagraphWithFormatChanges tracks. For the summary page. */
export function formatDiffers(
  origRich: { pPrXml: string; richTokens: { word: string; rPrXml: string }[] } | undefined,
  revRich: { pPrXml: string; richTokens: { word: string; rPrXml: string }[] } | undefined,
): boolean {
  if (!origRich || !revRich) return false;
  if (normalizePPr(origRich.pPrXml) !== normalizePPr(revRich.pPrXml) && !/<w:pPrChange\b/.test(revRich.pPrXml || '')) return true;
  const n = Math.min(origRich.richTokens.length, revRich.richTokens.length);
  for (let i = 0; i < n; i++) {
    const w = revRich.richTokens[i].word;
    if (formatKey(origRich.richTokens[i].rPrXml, w) !== formatKey(revRich.richTokens[i].rPrXml, w)) return true;
  }
  return false;
}

export function emitEqualParagraphWithFormatChanges(
  origRich: { pPrXml: string; richTokens: { word: string; rPrXml: string }[]; rawXml: string | null },
  revRich: { pPrXml: string; richTokens: { word: string; rPrXml: string }[]; rawXml: string | null },
): string | null {
  if (!_useTrackChanges || !origRich || !revRich || !revRich.rawXml) return null;

  const origPPrNorm = normalizePPr(origRich.pPrXml);
  const revPPrNorm = normalizePPr(revRich.pPrXml);
  // Don't add our own pPrChange if the source already tracks a paragraph-format
  // change — only one pPrChange is allowed and a second would corrupt the file.
  const pPrChanged = origPPrNorm !== revPPrNorm && !/<w:pPrChange\b/.test(revRich.pPrXml || '');

  // Check if any run formatting changed
  let anyRPrChanged = false;
  const minTokens = Math.min(origRich.richTokens.length, revRich.richTokens.length);
  for (let i = 0; i < minTokens; i++) {
    const w = revRich.richTokens[i].word;
    if (formatKey(origRich.richTokens[i].rPrXml, w) !== formatKey(revRich.richTokens[i].rPrXml, w)) {
      anyRPrChanged = true;
      break;
    }
  }

  if (!pPrChanged && !anyRPrChanged) return null;

  recChange('f', { type: 'format', scope: 'format' });
  return emitEqualFormat_node(origRich, revRich, pPrChanged, anyRPrChanged);
}

type RichLike = { pPrXml: string; richTokens: { word: string; rPrXml: string }[]; rawXml: string | null };

function emitEqualFormat_node(origRich: RichLike, revRich: RichLike, pPrChanged: boolean, anyRPrChanged: boolean): string {
  const p = parseEl(revRich.rawXml!);
  const doc = p.ownerDocument!;

  if (pPrChanged) {
    let pPr = directChild(p, 'pPr');
    if (!pPr) { pPr = makeEl(doc, 'w:pPr'); p.insertBefore(pPr, p.firstChild); }
    if (!directChild(pPr, 'pPrChange')) {
      const change = makeEl(doc, 'w:pPrChange'); setRevAttrs(change);
      change.appendChild(importEl(doc, origRich.pPrXml ? stripToPPrBase(origRich.pPrXml) : '<w:pPr/>'));
      pPr.appendChild(change);
    }
  }

  if (anyRPrChanged && origRich.rawXml) {
    // Compare run-by-run against the ORIGINAL paragraph's runs by ordinal —
    // richTokens are per WORD, so indexing runs with them misattributes the
    // change. When the run structures don't align, skip: an untracked format
    // change is better than an rPrChange recording the wrong prior formatting.
    try {
      const origP = parseEl(origRich.rawXml);
      const serializeDirectRPr = (r: Element): string => {
        const el = directChild(r, 'rPr');
        return el ? serializeClean(el) : '';
      };
      const topRuns = (root: Element): Element[] => wEls(root, 'r').filter(r => {
        for (let n: Node | null = r.parentNode; n && (n as Element).nodeType === 1; n = n.parentNode) {
          const e = n as Element;
          if (e.localName === 'r' && e.namespaceURI === W_NS) return false;
        }
        return true;
      });
      const revRuns = topRuns(p);
      const origRuns = topRuns(origP);
      // The runs' text, character by character (a tab or break counts as one).
      const runText = (r: Element): string => Array.from(r.childNodes).map(c => {
        const e = c as Element;
        if (e.nodeType !== 1 || e.namespaceURI !== W_NS) return '';
        if (e.localName === 't') return e.textContent || '';
        return e.localName === 'tab' || e.localName === 'br' || e.localName === 'cr' ? '\t' : '';
      }).join('');
      const origTexts = origRuns.map(runText), revTexts = revRuns.map(runText);
      const addChange = (run: Element, origRPrXml: string) => {
        let rPr = directChild(run, 'rPr');
        if (rPr && directChild(rPr, 'rPrChange')) return; // already records a change
        if (!rPr) { rPr = makeEl(doc, 'w:rPr'); run.insertBefore(rPr, run.firstChild); }
        const change = makeEl(doc, 'w:rPrChange'); setRevAttrs(change);
        change.appendChild(importEl(doc, rPrSnapshot(origRPrXml)));
        rPr.appendChild(change);
      };
      // Same text, runs split differently (a phrase made bold splits its run): compare each
      // revised run with the original formatting of the characters it holds. Characters whose
      // original formatting was mixed are left untracked rather than mistracked.
      // `key` maps a run's text to the characters the two versions share: all of them when the
      // texts are identical; only the non-blank ones when the paragraph is equal word for word
      // but its spacing differs ("1.<tab>Option" became "1. Option").
      const byChars = (key: (s: string) => string) => {
        const origSpans: { start: number; end: number; rPr: string }[] = [];
        let at = 0;
        origRuns.forEach((r, i) => { const n = key(origTexts[i]).length; origSpans.push({ start: at, end: at + n, rPr: serializeDirectRPr(r) }); at += n; });
        at = 0;
        revRuns.forEach((run, i) => {
          const start = at, end = at + key(revTexts[i]).length;
          at = end;
          if (start === end) return;
          const under = origSpans.filter(s => s.start < end && start < s.end);
          const kinds = new Set(under.map(s => formatKey(s.rPr || '<w:rPr></w:rPr>', revTexts[i])));
          if (kinds.size !== 1) return;
          const origRPrXml = under[0].rPr;
          if (formatKey(origRPrXml || '<w:rPr></w:rPr>', revTexts[i]) === formatKey(serializeDirectRPr(run) || '<w:rPr></w:rPr>', revTexts[i])) return;
          addChange(run, origRPrXml);
        });
      };
      const sameRuns = revRuns.length === origRuns.length && revTexts.every((t, i) => t === origTexts[i]);
      const nonBlank = (s: string) => s.replace(/\s+/g, '');
      if (!sameRuns && origTexts.join('') === revTexts.join('')) {
        byChars((s) => s);
      } else if (!sameRuns) {
        // Runs pair up by position only when they hold the same text. Otherwise (the spacing
        // differs) line the runs up by their non-blank characters; if even those differ (a quote
        // or dash style changed), leave the formatting untracked rather than record the
        // formatting of whatever run happens to sit at the same index.
        if (nonBlank(origTexts.join('')) === nonBlank(revTexts.join(''))) byChars(nonBlank);
      } else {
        for (let i = 0; i < revRuns.length; i++) {
          const run = revRuns[i];
          const origRPrXml = serializeDirectRPr(origRuns[i]);
          const revRPrXml = serializeDirectRPr(run);
          if (formatKey(origRPrXml || '<w:rPr></w:rPr>', revTexts[i]) === formatKey(revRPrXml || '<w:rPr></w:rPr>', revTexts[i])) continue;
          let rPr = directChild(run, 'rPr');
          if (rPr && directChild(rPr, 'rPrChange')) continue; // already records a change
          if (!rPr) { rPr = makeEl(doc, 'w:rPr'); run.insertBefore(rPr, run.firstChild); }
          const change = makeEl(doc, 'w:rPrChange'); setRevAttrs(change);
          change.appendChild(importEl(doc, rPrSnapshot(origRPrXml)));
          rPr.appendChild(change);
        }
      }
    } catch { /* leave run formatting untracked rather than mistracked */ }
  }
  return serializeClean(p);
}

